import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SocialAuthCallback = ({ onSuccess, onError }) => {
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Completing sign in...');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      const params = new URLSearchParams(window.location.search);
      
      try {
        let response;

        // Google callback (Emergent Auth) - session_id in hash
        if (hash.includes('session_id=')) {
          const sessionId = hash.split('session_id=')[1]?.split('&')[0];
          if (!sessionId) throw new Error('No session ID found');
          
          setMessage('Verifying Google account...');
          response = await axios.post(`${API}/auth/social/google`, { session_id: sessionId });
        }
        // Facebook callback
        else if (path.includes('/auth/callback/facebook')) {
          const code = params.get('code');
          if (!code) throw new Error('No authorization code found');
          
          setMessage('Verifying Facebook account...');
          response = await axios.post(`${API}/auth/social/facebook`, {
            provider: 'facebook',
            code,
            redirect_uri: window.location.origin + '/auth/callback/facebook'
          });
        }
        // Instagram callback
        else if (path.includes('/auth/callback/instagram')) {
          const code = params.get('code');
          if (!code) throw new Error('No authorization code found');
          
          setMessage('Verifying Instagram account...');
          response = await axios.post(`${API}/auth/social/instagram`, {
            provider: 'instagram',
            code,
            redirect_uri: window.location.origin + '/auth/callback/instagram'
          });
        }
        // TikTok callback
        else if (path.includes('/auth/callback/tiktok')) {
          const code = params.get('code');
          const state = params.get('state');
          const savedState = sessionStorage.getItem('tiktok_csrf');
          
          if (state !== savedState) throw new Error('Invalid state parameter');
          if (!code) throw new Error('No authorization code found');
          
          sessionStorage.removeItem('tiktok_csrf');
          setMessage('Verifying TikTok account...');
          response = await axios.post(`${API}/auth/social/tiktok`, {
            provider: 'tiktok',
            code,
            redirect_uri: window.location.origin + '/auth/callback/tiktok'
          });
        }
        // Reddit callback
        else if (path.includes('/auth/callback/reddit')) {
          const code = params.get('code');
          const state = params.get('state');
          const savedState = sessionStorage.getItem('reddit_state');
          
          if (state !== savedState) throw new Error('Invalid state parameter');
          if (!code) throw new Error('No authorization code found');
          
          sessionStorage.removeItem('reddit_state');
          setMessage('Verifying Reddit account...');
          response = await axios.post(`${API}/auth/social/reddit`, {
            provider: 'reddit',
            code,
            redirect_uri: window.location.origin + '/auth/callback/reddit'
          });
        }
        // Generic callback (for Google redirect to /auth/callback)
        else if (path === '/auth/callback' && hash.includes('session_id=')) {
          const sessionId = hash.split('session_id=')[1]?.split('&')[0];
          if (!sessionId) throw new Error('No session ID found');
          
          setMessage('Verifying Google account...');
          response = await axios.post(`${API}/auth/social/google`, { session_id: sessionId });
        }
        else {
          throw new Error('Unknown callback type');
        }

        if (response?.data?.token && response?.data?.user) {
          setStatus('success');
          setMessage('Success! Redirecting...');
          
          // Store token and call success handler
          localStorage.setItem('token', response.data.token);
          
          // Clean URL and redirect
          window.history.replaceState({}, document.title, '/');
          
          if (onSuccess) {
            onSuccess(response.data.token, response.data.user);
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        console.error('Social auth error:', err);
        setStatus('error');
        const d5 = err.response?.data?.detail;
        setMessage(typeof d5 === 'string' ? d5 : err.message || 'Authentication failed');
        
        if (onError) {
          onError(err.response?.data?.detail || err.message);
        }
        
        // Redirect back to login after delay
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    };

    processCallback();
  }, [onSuccess, onError]);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-800">
      {/* Black Header */}
      <div className="bg-black py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center">
          <img src="/logo-v4.png" alt="Logo" className="h-20 w-20 object-contain" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-[#FF6600]/10 p-8 border border-white/50 text-center">
            {status === 'processing' && (
              <>
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 border-3 border-[#FF6600] border-t-transparent rounded-full animate-spin"></div>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Signing you in...</h2>
                <p className="text-gray-500 dark:text-gray-400">{message}</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Welcome!</h2>
                <p className="text-gray-500 dark:text-gray-400">{message}</p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Sign in failed</h2>
                <p className="text-gray-500 dark:text-gray-400 mb-4">{message}</p>
                <p className="text-sm text-gray-400">Redirecting you back...</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialAuthCallback;
